import { useCallback } from "react";
import { InsuranceAPIPromiseClient } from "../../../gen/witbooking/backoffice/insurance/v1/insurance_api_grpc_web_pb";
import { useGrpcClient, usePropertyGrpcCall } from "../../../lib/grpc";
import {
  CreateInsuranceRequest,
  DeleteInsuranceRequest,
  GetAllInsurancesRequest,
  GetInsuranceRequest,
  ReorderInsuranceRequest,
  UpdateInsuranceRequest,
} from "../../../gen/witbooking/backoffice/insurance/v1/insurance_api_pb";
import { useMapper } from "./mapper.hook";
import { InsuranceEntity } from "../entity";

export function useInsuranceApi() {
  const { toObject, toGrpc } = useMapper();

  const grpcClient = useGrpcClient(InsuranceAPIPromiseClient);
  const getAllInsurances = usePropertyGrpcCall(grpcClient, grpcClient.getAllInsurances);
  const getOneInsurances = usePropertyGrpcCall(grpcClient, grpcClient.getInsurance);
  const deleteInsurance = usePropertyGrpcCall(grpcClient, grpcClient.deleteInsurance);
  const createInsurance = usePropertyGrpcCall(grpcClient, grpcClient.createInsurance);
  const updateInsurance = usePropertyGrpcCall(grpcClient, grpcClient.updateInsurance);
  const reorderInsurance = usePropertyGrpcCall(grpcClient, grpcClient.reorderInsurance);

  return {
    onGetAll: useCallback(async () => {
      const request = new GetAllInsurancesRequest();
      const response = await getAllInsurances(request);
      return response.getInsurancesList().map(it => toObject(it));
    }, [getAllInsurances, toObject]),

    onGetOne: useCallback(
      async (id: string) => {
        const request = new GetInsuranceRequest();
        request.setId(id);
        const response = await getOneInsurances(request);
        if (!response.hasInsurance()) {
          throw new Error("Insurance not found");
        }
        return toObject(response.getInsurance()!);
      },
      [getOneInsurances, toObject]
    ),

    onDelete: useCallback(
      async (id: string) => {
        const request = new DeleteInsuranceRequest();
        request.setId(id);
        await deleteInsurance(request);
      },
      [deleteInsurance]
    ),

    onReOrder: useCallback(
      async (beforeId: string, afterId: string) => {
        const request = new ReorderInsuranceRequest();
        request.setBeforeId(beforeId);
        request.setAfterId(afterId);
        await reorderInsurance(request);
      },
      [reorderInsurance]
    ),

    onUpdate: useCallback(
      async (data: InsuranceEntity) => {
        console.info("Updating insurance...", data);
        const request = new UpdateInsuranceRequest();
        request.setId(data.id);
        request.setInsurance(toGrpc(data));
        await updateInsurance(request);
      },
      [toGrpc, updateInsurance]
    ),

    onCreate: useCallback(
      async (data: InsuranceEntity) => {
        console.info("Creating insurance...", data);
        const request = new CreateInsuranceRequest();
        request.setInsurance(toGrpc(data));
        await createInsurance(request);
      },
      [createInsurance, toGrpc]
    ),
  };
}
